// @flow
import React from 'react';
import { checkPropTypes } from './prop-spec.js';
import {
  ValidationErrorsMethods,
  // type ValidationError,
  type LocationData,
} from './ValidationContext.js';
import type { ComponentWithSpecification } from './types.js';

// type AddErrors = (maybeErrors: ValidationError[] | void) => void;
// We want this to be defined like the line above, but we need to refactor all the
// call sites first to ensure fallback location data is provided.
type DefectiveAddErrors = (maybeErrors: Object[] | void) => void;

export function usePropsValidation<Props>(
  props: Config<Props, {}>,
  component: ComponentWithSpecification<Config<Props, {}>>,
): {|
  failedValidation: boolean,
  addErrors: DefectiveAddErrors,
  locations: Object,
  propValidationStatus: $ObjMap<Props, () => boolean>,
|} {
  const { addErrors } = React.useContext(ValidationErrorsMethods);
  const defaultPropValidationStatus = Object.fromEntries(
    Object.entries(props).map(([k]) => [k, true]),
  );

  React.useEffect(() => {
    if (!props._performValidation || props._sourceLocations === undefined) {
      return;
    }
    const [maybeErrors] = checkPropTypes(
      component.spec,
      props,
      component.name,
      props._sourceLocations,
    );
    if (maybeErrors) addErrors(maybeErrors);
  });

  if (!props._performValidation || props._sourceLocations === undefined) {
    return {
      failedValidation: false,
      addErrors() {},
      locations: {},
      propValidationStatus: defaultPropValidationStatus,
    };
  }

  const [maybeErrors, rootErrors] = checkPropTypes(
    component.spec,
    props,
    component.name,
    props._sourceLocations,
  );

  // NOTE this is only to support ad-hoc assertions (including async assertions)
  // until I figure out a nice way to make checkPropTypes specs more composable
  // and async-capable.
  return {
    failedValidation: maybeErrors && maybeErrors.length > 0,
    addErrors,
    locations: props._sourceLocations,
    propValidationStatus: {
      ...defaultPropValidationStatus,
      ...Object.fromEntries(
        rootErrors.map(({ propName }) => [propName, false]),
      ),
    },
  };
}

type Validator = (AddErrors, locations: Object) => void;

export function useCustomValidation(
  props: Object,
  validator: Validator,
): {|
  failedValidation: boolean,
|} {
  const { addErrors } = React.useContext(ValidationErrorsMethods);
  React.useEffect(() => {
    if (!props._performValidation || props._sourceLocations === undefined) {
      return;
    }
    let errors = [];
    validator((errorOrErrors) => {
      errors = errors.concat(errorOrErrors);
    }, props._sourceLocations);
    if (errors.length) addErrors(errors);
  });

  if (!props._performValidation || props._sourceLocations === undefined) {
    return { failedValidation: false };
  }

  let errors = [];
  validator((err) => {
    errors = errors.concat(err);
  }, props._sourceLocations);
  return { failedValidation: Boolean(errors.length) };
}

// Our access to location data is very dynamic, so we can't
// can't give flow strong guarantees about their existence.
// This fallback location (which will show errors at the
// root of the document) is largely to appease flow, but in
// the unlikely case that our dynamic lookup *does* fail at
// runtime, our errors will still have a place to annotate
// the document (albeit a far less useful annotation location)
export const fallbackLocationData: LocationData = {
  startLineNumber: 1,
  startColumn: 0,
  endLineNumber: 1,
  endColumn: 80,
};
